import { useEffect } from 'react';
import { useLiveEvents } from '@/lib/live';
import { toast } from '@/lib/toast';
import { fetchRestore } from './api';
import { backupWhenInline } from './format';

// After a restore every open tab reloads, because all the data under it changed at once. The server announces it
// with a live event; this listener is mounted for the whole session and shows a note once the page is back.

const FLAG = 'clientbook.backups.restored';

/** Restores started from this tab, so it can say "your" restore finished rather than someone else's. */
const ownRestores = new Set<string>();
let reloading = false;

interface ReloadNote {
  own: boolean;
  dataFrom: string | null;
  secretsKept: boolean;
}

export function markOwnRestore(id: string): void {
  ownRestores.add(id);
}

/** Reloads this tab once, leaving a note for after the reload. */
export function reloadAfterRestore(id: string, dataFrom: string | null, known?: { secretsKept: boolean }): void {
  if (reloading) return;
  reloading = true;
  const own = ownRestores.has(id);
  const go = (secretsKept: boolean) => {
    const note: ReloadNote = { own, dataFrom, secretsKept };
    try {
      sessionStorage.setItem(FLAG, JSON.stringify(note));
    } catch {
      // Private windows may refuse storage; the reload still happens.
    }
    window.location.reload();
  };
  if (!own) {
    setTimeout(() => go(false), 400);
    return;
  }
  // Your own restore: leave the finished state on screen for a moment, and find out whether saved keys were kept.
  const started = Date.now();
  const lookup = known ? Promise.resolve(known.secretsKept) : fetchRestore(id).then((r) => r.secretsKept, () => false);
  void lookup.then((secretsKept) => setTimeout(() => go(secretsKept), Math.max(0, 1400 - (Date.now() - started))));
}

export function RestoreReload() {
  useLiveEvents((event) => {
    if (event.type !== 'invalidate') return;
    for (const key of event.keys) {
      if (key[0] === 'backups' && key[1] === 'restored' && typeof key[2] === 'string') {
        reloadAfterRestore(key[2], typeof key[3] === 'string' ? key[3] : null);
      }
    }
  });

  useEffect(() => {
    let note: ReloadNote | null = null;
    try {
      const raw = sessionStorage.getItem(FLAG);
      sessionStorage.removeItem(FLAG);
      note = raw ? (JSON.parse(raw) as ReloadNote) : null;
    } catch {
      note = null;
    }
    if (!note) return;
    const when = note.dataFrom ? backupWhenInline(note.dataFrom) : null;
    if (note.own) {
      toast(when ? `Restore complete · data from ${when} · private key discarded` : 'Restore complete · private key discarded', { icon: 'check-circle' });
      if (note.secretsKept) {
        toast('The backup came from a server with another master key, so your saved Google, AWS and AI keys stayed as they were. Reconnect inboxes if replies stop arriving.', {
          icon: 'info',
        });
      }
    } else {
      toast(when ? `Clientbook reloaded with data restored from the backup of ${when}` : 'Clientbook reloaded with restored data', { icon: 'restore' });
    }
  }, []);

  return null;
}
